// src/components/GrowthStats.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs } from "firebase/firestore";
import { format, startOfDay, subDays, isWithinInterval } from "date-fns";
import {
  FaChartLine,
  FaTasks,
  FaCheckCircle,
  FaFire,
  FaChevronUp,
  FaChevronDown,
  FaStar,
  FaBolt,
  FaMedal,
  FaTrophy,
} from "react-icons/fa";

const Container = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 14px;
  padding: 40px 20px;
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  z-index: 0;
  gap: 15px;
  color: var(--fontPrimaryColor);
`;

const Header = styled.h2`
  text-align: center;
  font-weight: 500;
  margin: 0;
  font-size: 18px;
  margin-bottom: 20px;
  font-family: "Montserrat", sans-serif;
  color: var(--fontPrimaryColor);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  flex: 1;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${({ color }) => `${color}40`};
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${({ color }) => color} 0%,
      ${({ color }) => `${color}80`} 100%
    );
    border-radius: 12px 12px 0 0;
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ color }) => `${color}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ color }) => `${color}40`};

  svg {
    color: ${({ color }) => color};
    font-size: 1.2rem;
  }
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--fontPrimaryColor);
  margin: 0;
  margin-bottom: 3px;
`;

const StatPeriod = styled.span`
  font-size: 0.75rem;
  color: var(--fontSecondaryColor);
  opacity: 0.8;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ color }) => color};
  margin: 5px 0;
`;

const StatSubtitle = styled.p`
  font-size: 0.8rem;
  color: var(--fontSecondaryColor);
  margin: 0;
  line-height: 1.4;
`;

const TrendBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${({ positive }) =>
    positive ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)"};
  color: ${({ positive }) => (positive ? "#10b981" : "#ef4444")};
  border: 1px solid
    ${({ positive }) =>
      positive ? "rgba(16, 185, 129, 0.3)" : "rgba(239, 68, 68, 0.3)"};
`;

const AdditionalInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.75rem;
  color: var(--fontSecondaryColor);

  svg {
    font-size: 0.9rem;
    color: ${({ color }) => color};
  }
`;

const LoadingSkeleton = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--fontSecondaryColor);
  font-size: 0.9rem;
`;

// Helper function to safely convert Firestore timestamps
const toDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === "function") return val.toDate();
  if (val instanceof Date) return val;
  try {
    return new Date(val);
  } catch {
    return null;
  }
};

const dateKeyToDate = (dateKey) => {
  if (!dateKey || typeof dateKey !== "string") return null;
  const d = new Date(dateKey + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : startOfDay(d);
};

const isDateInRange = (date, startDate, endDate) => {
  if (!date) return false;
  return isWithinInterval(startOfDay(date), {
    start: startDate,
    end: endDate,
  });
};

const countCompletedDatesInRange = (completedDates, startDate, endDate) => {
  if (!completedDates || typeof completedDates !== "object") return 0;
  let count = 0;
  for (const [dateKey, value] of Object.entries(completedDates)) {
    if (!value) continue;
    const d = dateKeyToDate(dateKey);
    if (d && isWithinInterval(d, { start: startDate, end: endDate })) {
      count += 1;
    }
  }
  return count;
};

const countTaskCompletionsInRange = (task, startDate, endDate) => {
  if (!task) return 0;

  // Recurring/sticky tasks track completion per-day in completedDates
  if (task.recurring || task.sticky) {
    return countCompletedDatesInRange(task.completedDates, startDate, endDate);
  }

  // One-time tasks: completed is a single boolean; approximate completion day by task.date (preferred) else createdAt
  if (!task.completed) return 0;

  const completionDate = task.date || task.createdAt;
  return isDateInRange(completionDate, startDate, endDate) ? 1 : 0;
};

const countHabitCompletionsInRange = (habit, startDate, endDate) => {
  if (!habit) return 0;
  return countCompletedDatesInRange(habit.completedDates, startDate, endDate);
};

const GrowthStats = () => {
  const { userId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [dashboardTasks, setDashboardTasks] = useState([]);
  const [clientTasks, setClientTasks] = useState([]);
  const [habits, setHabits] = useState([]);
  const [timePeriod, setTimePeriod] = useState("week");

  // Fetch all data
  useEffect(() => {
    if (!userId) return;

    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch dashboard tasks
        const dashboardTasksRef = collection(
          db,
          "users",
          userId,
          "dashboardTasks"
        );
        const dashboardTasksSnapshot = await getDocs(dashboardTasksRef);
        const dashboardTasksData = dashboardTasksSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: toDate(doc.data().createdAt),
          date: toDate(doc.data().date),
          stopRecurrenceDate: toDate(doc.data().stopRecurrenceDate),
        }));
        setDashboardTasks(dashboardTasksData);

        // Fetch client tasks
        const clientsSnapshot = await getDocs(
          collection(db, "users", userId, "clients")
        );
        const clientTasksData = [];

        for (const clientDoc of clientsSnapshot.docs) {
          const clientTasksRef = collection(
            db,
            "users",
            userId,
            "clients",
            clientDoc.id,
            "tasks"
          );
          const clientTasksSnapshot = await getDocs(clientTasksRef);
          clientTasksData.push(
            ...clientTasksSnapshot.docs.map((doc) => ({
              id: doc.id,
              clientId: clientDoc.id,
              ...doc.data(),
              createdAt: toDate(doc.data().createdAt),
              date: toDate(doc.data().date),
            }))
          );
        }
        setClientTasks(clientTasksData);

        // Fetch habits
        const habitsRef = collection(db, "users", userId, "habits");
        const habitsSnapshot = await getDocs(habitsRef);
        const habitsData = habitsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          startDate: toDate(doc.data().startDate),
        }));
        setHabits(habitsData);
      } catch (err) {
        console.error("Error fetching growth data:", err);
        setError("Failed to load growth data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [userId]);

  // Get date range based on selected period
  const getDateRange = () => {
    const endDate = startOfDay(new Date());
    let startDate;

    switch (timePeriod) {
      case "week":
        startDate = subDays(endDate, 6);
        break;
      case "month":
        startDate = subDays(endDate, 29);
        break;
      case "quarter":
        startDate = subDays(endDate, 89);
        break;
      case "year":
        startDate = subDays(endDate, 364);
        break;
      default:
        startDate = subDays(endDate, 6);
    }

    return { startDate: startOfDay(startDate), endDate: startOfDay(endDate) };
  };

  // Calculate Growth Score (Total completed tasks + habits)
  const calculateGrowthScore = () => {
    const { startDate, endDate } = getDateRange();

    const allTasks = [...dashboardTasks, ...clientTasks];
    const tasksCompleted = allTasks.reduce(
      (sum, task) =>
        sum + countTaskCompletionsInRange(task, startDate, endDate),
      0
    );

    const habitsCompleted = habits.reduce(
      (sum, habit) =>
        sum + countHabitCompletionsInRange(habit, startDate, endDate),
      0
    );

    return tasksCompleted + habitsCompleted;
  };

  // Calculate Total Tasks Completed
  const calculateTotalTasksCompleted = () => {
    const { startDate, endDate } = getDateRange();

    const allTasks = [...dashboardTasks, ...clientTasks];
    return allTasks.reduce(
      (sum, task) =>
        sum + countTaskCompletionsInRange(task, startDate, endDate),
      0
    );
  };

  // Calculate Total Habits Completed
  const calculateTotalHabitsCompleted = () => {
    const { startDate, endDate } = getDateRange();

    return habits.reduce(
      (sum, habit) =>
        sum + countHabitCompletionsInRange(habit, startDate, endDate),
      0
    );
  };

  // Calculate Most Productive Day
  const calculateMostProductiveDayStats = () => {
    const { startDate, endDate } = getDateRange();

    const completionsByDateKey = new Map();

    const addIfInRange = (date) => {
      if (!date) return;
      if (!isDateInRange(date, startDate, endDate)) return;
      const key = format(startOfDay(date), "yyyy-MM-dd");
      completionsByDateKey.set(key, (completionsByDateKey.get(key) || 0) + 1);
    };

    const addDateKeyIfInRange = (dateKey) => {
      const d = dateKeyToDate(dateKey);
      if (!d) return;
      if (!isWithinInterval(d, { start: startDate, end: endDate })) return;
      completionsByDateKey.set(
        dateKey,
        (completionsByDateKey.get(dateKey) || 0) + 1
      );
    };

    const allTasks = [...dashboardTasks, ...clientTasks];
    allTasks.forEach((task) => {
      if (task?.completedDates && (task.recurring || task.sticky)) {
        for (const [dateKey, value] of Object.entries(task.completedDates)) {
          if (!value) continue;
          addDateKeyIfInRange(dateKey);
        }
        return;
      }

      // One-time tasks: use task.date (preferred) else createdAt as best-effort
      if (task?.completed) {
        addIfInRange(task.date || task.createdAt);
      }
    });

    habits.forEach((habit) => {
      if (!habit?.completedDates) return;
      for (const [dateKey, value] of Object.entries(habit.completedDates)) {
        if (!value) continue;
        addDateKeyIfInRange(dateKey);
      }
    });

    let bestCount = 0;

    for (const [, count] of completionsByDateKey.entries()) {
      if (count > bestCount) {
        bestCount = count;
      }
    }

    return bestCount;
  };

  // Calculate simple percentage change for trend
  const calculateTrend = () => {
    if (timePeriod === "week") return 0;

    const currentScore = calculateGrowthScore();

    // Get previous period score
    const today = startOfDay(new Date());
    let previousStartDate, previousEndDate;

    switch (timePeriod) {
      case "month":
        previousStartDate = subDays(today, 60);
        previousEndDate = subDays(today, 30);
        break;
      case "quarter":
        previousStartDate = subDays(today, 180);
        previousEndDate = subDays(today, 90);
        break;
      case "year":
        previousStartDate = subDays(today, 730);
        previousEndDate = subDays(today, 365);
        break;
      default:
        return 0;
    }

    // Calculate previous period score
    const allTasks = [...dashboardTasks, ...clientTasks];
    const previousTasksCompleted = allTasks.reduce(
      (sum, task) =>
        sum +
        countTaskCompletionsInRange(task, previousStartDate, previousEndDate),
      0
    );

    const previousHabitsCompleted = habits.reduce(
      (sum, habit) =>
        sum +
        countHabitCompletionsInRange(habit, previousStartDate, previousEndDate),
      0
    );

    const previousScore = previousTasksCompleted + previousHabitsCompleted;

    if (previousScore === 0) return currentScore > 0 ? 100 : 0;

    const change = ((currentScore - previousScore) / previousScore) * 100;
    return Math.round(change);
  };

  // Calculate completion rate
  const calculateCompletionRate = () => {
    const growthScore = calculateGrowthScore();
    let maxPossible = 0;

    // Simple estimation - assume at least one task/habit per day is expected
    const { startDate, endDate } = getDateRange();
    const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    maxPossible = days * 2; // At least 1 task and 1 habit per day

    if (maxPossible === 0) return 0;
    return Math.min(Math.round((growthScore / maxPossible) * 100), 100);
  };

  if (loading) {
    return (
      <Container>
        <Header>Growth Stats</Header>
        <LoadingSkeleton>Loading growth statistics...</LoadingSkeleton>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>Growth Stats</Header>
        <LoadingSkeleton>{error}</LoadingSkeleton>
      </Container>
    );
  }

  const growthScore = calculateGrowthScore();
  const totalTasksCompleted = calculateTotalTasksCompleted();
  const totalHabitsCompleted = calculateTotalHabitsCompleted();
  const bestCount = calculateMostProductiveDayStats();
  const trend = calculateTrend();
  const completionRate = calculateCompletionRate();

  const getPeriodText = () => {
    switch (timePeriod) {
      case "week":
        return "Last 7 days";
      case "month":
        return "Last 30 days";
      case "quarter":
        return "Last 90 days";
      case "year":
        return "Last 365 days";
      default:
        return "Last 7 days";
    }
  };

  return (
    <Container>
      <Header>Growth Stats</Header>
      <StatsGrid>
        {/* Box 1: Growth Score */}
        <StatCard color="#3b83f60">
          <StatHeader>
            <IconWrapper color="#3b82f6">
              <FaChartLine />
            </IconWrapper>
            <StatInfo>
              <StatTitle>Growth Score</StatTitle>
              <StatPeriod>{getPeriodText()}</StatPeriod>
            </StatInfo>
            <TrendBadge positive={trend >= 0}>
              {trend >= 0 ? <FaChevronUp /> : <FaChevronDown />}
              {Math.abs(trend)}%
            </TrendBadge>
          </StatHeader>
          <StatValue color="#3b82f6">{growthScore}</StatValue>
          <StatSubtitle>Total completed activities</StatSubtitle>
          <AdditionalInfo color="#3b82f6">
            <FaStar /> <span>{completionRate}% completion rate</span>
          </AdditionalInfo>
        </StatCard>

        {/* Box 2: Tasks Completed */}
        <StatCard color="#10b9810">
          <StatHeader>
            <IconWrapper color="#10b981">
              <FaTasks />
            </IconWrapper>
            <StatInfo>
              <StatTitle>Tasks Completed</StatTitle>
              <StatPeriod>{getPeriodText()}</StatPeriod>
            </StatInfo>
          </StatHeader>
          <StatValue color="#10b981">{totalTasksCompleted}</StatValue>
          <StatSubtitle>Finished across all projects</StatSubtitle>
          <AdditionalInfo color="#10b981">
            <FaBolt /> <span>Consistent execution</span>
          </AdditionalInfo>
        </StatCard>

        {/* Box 3: Habits Completed */}
        <StatCard color="#422d090">
          <StatHeader>
            <IconWrapper color="#f59e0b">
              <FaCheckCircle />
            </IconWrapper>
            <StatInfo>
              <StatTitle>Habits Completed</StatTitle>
              <StatPeriod>{getPeriodText()}</StatPeriod>
            </StatInfo>
          </StatHeader>
          <StatValue color="#f59e0b">{totalHabitsCompleted}</StatValue>
          <StatSubtitle>Daily routines maintained</StatSubtitle>
          <AdditionalInfo color="#f59e0b">
            <FaMedal /> <span>Building lasting routines</span>
          </AdditionalInfo>
        </StatCard>

        {/* Box 4: Peak Performance */}
        <StatCard color="#ef44440">
          <StatHeader>
            <IconWrapper color="#ef4444">
              <FaFire />
            </IconWrapper>
            <StatInfo>
              <StatTitle>Peak Performance</StatTitle>
              <StatPeriod>Record day</StatPeriod>
            </StatInfo>
          </StatHeader>
          <StatValue color="#ef4444">{bestCount}</StatValue>
          <StatSubtitle>
            {bestCount > 0 ? "Most completions in a day" : "No completions yet"}
          </StatSubtitle>
          {bestCount > 0 && (
            <AdditionalInfo color="#ef4444">
              <FaTrophy /> <span>Record productivity</span>
            </AdditionalInfo>
          )}
        </StatCard>
      </StatsGrid>
    </Container>
  );
};

export default GrowthStats;
